import { NETWORK_STATUS } from '../constants';
import { UnauthorizedError } from './unauthorizedError';

export class GraphQLTokenService {
  // eslint-disable-next-line
  constructor(private signalError: (e: unknown) => unknown) {}

  public fetchGraphQLToken = ensureSingleRequestInFlight(async () => {
    try {
      // The graphql token cookie is set by the monolith when a request to /auth/jwt is made
      await fetch('/auth/jwt', {
        method: 'POST',
      });
    } catch (e) {
      this.signalError(e);
    }
  });

  public static async throwAuthErrorIfNeeded(response: Response) {
    if (response.status === NETWORK_STATUS.UNAUTHORIZED) {
      throw new UnauthorizedError();
    }

    // There is a bug in unified API when you logout and login again with different user in the same browser,
    // the `graphql_token` is not cleared from the cookies, and if we send a request using that token,
    // the response will be 200 OK, but the response body will contain a GraphQL unauthorized error instead of 401
    // So to handle this case, we need to check the response body for GraphQL unauthorized errors
    if (response.ok && response.headers.get('content-type')?.includes('application/json')) {
      try {
        const responseClone = response.clone();
        const data = await responseClone.json();

        if (data.errors && Array.isArray(data.errors)) {
          for (const err of data.errors) {
            switch (err.extensions?.code) {
              case 'UNAUTHORIZED':
                throw new UnauthorizedError();
              default:
                break;
            }
          }
        }
      } catch (e) {
        // If we can't parse JSON or other error, ignore and continue
        // (but don't ignore UnauthorizedError we just threw)
        if (e instanceof UnauthorizedError) {
          throw e;
        }
      }
    }
  }

  public async provideRetry<T>(fetcher: () => Promise<T>, shouldRetry = isUnauthorizedError) {
    let result!: T;

    try {
      result = await fetcher();
    } catch (e) {
      if (shouldRetry(e)) {
        await this.fetchGraphQLToken();
        result = await fetcher();
      } else {
        throw e;
      }
    }

    return result;
  }
}

function isUnauthorizedError(e: unknown) {
  if (!(e instanceof Error)) return false;

  // NOTE: You cannot assert e.constructor.name === 'UnauthorizedError' expliciatly because the minification of the code turns UnauthorizedError into a random string
  return e.constructor.name === UnauthorizedError.name;
}

function ensureSingleRequestInFlight<T = unknown>(requestFn: () => Promise<T>) {
  let isRequestInFlight = false;
  let lastPromise: Promise<T>;
  const modifiedRequestFn = async () => {
    if (isRequestInFlight) return lastPromise;
    isRequestInFlight = true;
    lastPromise = requestFn();
    await lastPromise;
    isRequestInFlight = false;
    return lastPromise;
  };
  return modifiedRequestFn;
}
